/**
 * StrategyPositionsModal — Classic strategy modal.
 *
 * Shows every leg of a single strategy as a dense table (one row per leg,
 * with Entry / Stop-Loss / Exit columns powered by `LegSection`). Opened
 * both from:
 *   - the Classic Schedule page (via `ClassicScheduleView` → `scheduleContext`)
 *   - the Positions page (via `PositionTableView` strategy-details popup)
 *
 * Mirrors the Pro view's `StrategyPositionsModalV2` structurally — shared header +
 * action toolbar — but renders a table body instead of a CSS grid with expand/collapse.
 */
import React, { useMemo } from 'react';
import { Ban, LogOut, RotateCcw, Unplug } from 'lucide-react';
import Badge from '../ui/Badge';
import { CHIP_DENSE } from '../../utils/chipColors';
import { EntrySourceBadge } from '../ui/EntrySourceBadge';
import { LegSection, formatLegTimestamp } from '../schedule/LegSection';
import { describeWatConfig, describeOrbConfig, orbHeaderLabel } from '../../utils/triggerDescribe';
import { exitTypeLabel } from '../../utils/exitTypeLabel';
import { Modal } from '../ui/Modal';
import { cn } from '../../utils/cn';
import PnlFlash from '../common/PnlFlash';
import {
  formatPrice,
  formatQtyWithLots,
  getDisplayStatus,
  isCancellable,
  missedExitLabel,
  ReSequenceBadge,
  statusBadgeVariant,
  compareLegPositions,
} from '../../utils/positionDisplay';
import type { PositionRow } from '../../types';
import { StrategyModalHeader } from '../schedule/shared/StrategyModalHeader';
import { StrategyActionToolbar } from '../schedule/shared/StrategyActionToolbar';
import {
  TH,
  TH_R,
  TH_C,
  SideBadge,
  slConfigDisplay,
  rowBg,
  rowOpacity,
} from './positionTableHelpers';
import { ulExitType, ulSlNoteHasOffset, ulSlState, ulSpotRows } from '../../utils/ulSpotLevels';
import {
  groupRowsByExecution,
  describeExecutionGroup,
  shortExecutionId,
} from '../../utils/executionGroup';

/**
 * Optional Schedule-page context. When passed, the modal renders a metadata
 * pill row below the title + a sticky banner above the rows with fill
 * progress, entry/exit countdown, and broker coverage. Absent when the modal
 * is opened from the Positions page — behaviour is unchanged there.
 */
export interface ScheduleModalContext {
  scheduledEntryTime: string | null;
  scheduledExitTime: string | null;
  scheduledExitDate: string | null;
  linkedEntryDate: string | null;
  tradingType: string;
  intradayExitMode: string;
  underlying: string;
  expiryType: string;
  productType: string;
  viewedDate: string;
  nowIstHms: string | null;
  brokerList: string | null;   // already truncated to "+ N more" by caller
  /**
   * Pre-computed run-state chip — replaces the "4/4 filled" fill-progress
   * label in the schedule ribbon with the canonical State chip shown in the
   * Schedule table (e.g. "RUNNING 3/4", "ERROR 1/4", "CLOSED 4/4").
   * Computed by `computeStrategyRunState` in the caller; absent when the
   * strategy has no positions yet (synthesized row).
   */
  runState?: { label: string; variant: 'green' | 'red' | 'gray' };
  fillProgress: {
    filled: number;
    rejected: number;
    cancelled: number;
    pending: number;
    total: number;
    variant: 'green' | 'yellow' | 'red' | 'gray';
  };
}

export interface StrategyPositionsModalProps {
  basketName: string;
  strategyName: string;
  /** When true, the modal header shows the basket label in violet + an MP
   *  pill, mirroring the Positions / Schedule list views. */
  isMarketplace?: boolean;
  positions: PositionRow[];
  onClose: () => void;
  onExit?: (p: PositionRow) => void;
  onRetry?: (p: PositionRow) => void;
  /** Platform-only "stop watching" per leg (never calls the broker). */
  onTerminate?: (p: PositionRow) => void;
  /** Strategy-level terminate for the toolbar's "Terminate" button. */
  onTerminateAll?: () => void;
  exitingId?: string | null;
  retryingId?: string | null;
  terminatingId?: string | null;
  /**
   * Optional bulk handlers for the toolbar's "Retry N" / "Exit N" buttons.
   * Receive the list of actionable position IDs so the caller can run a
   * single rich confirm + batched service call. When omitted, the toolbar
   * falls back to fanning the per-row `onRetry` / `onExit` over each row —
   * which triggers one confirm per position (legacy behaviour, noisy).
   */
  onRetryAll?: (positionIds: string[]) => void;
  onExitAll?: (positionIds: string[]) => void;
  /** Disables the bulk Retry/Exit toolbar buttons while a bulk call is in flight. */
  actionPending?: boolean;
  scheduleContext?: ScheduleModalContext;
  /**
   * When set, renders a "Back" button in the header that returns the user to
   * a parent modal (e.g. the basket-level modal on the Schedule page). The X
   * button still dismisses the entire modal stack via `onClose`.
   */
  onBack?: () => void;
  /** Optional label for the back button (defaults to "Back"). */
  backLabel?: string;
  /**
   * When set, renders a small refresh icon in the header. Called to force a
   * re-fetch of the underlying positions (used by the Schedule page to
   * manually bust its detail cache).
   */
  onRefresh?: () => void;
  /** Shows a spinning refresh indicator when true. */
  refreshing?: boolean;
}

export const StrategyPositionsModal: React.FC<StrategyPositionsModalProps> = ({
  basketName,
  strategyName,
  isMarketplace = false,
  positions,
  onClose,
  onExit,
  onRetry,
  onTerminate,
  onTerminateAll,
  onExitAll,
  onRetryAll,
  exitingId,
  retryingId,
  terminatingId,
  actionPending = false,
  scheduleContext,
  onBack,
  backLabel = 'Back',
  onRefresh,
  refreshing = false,
}) => {
  const showActions = !!(onExit || onRetry || onTerminate);
  const isTerminable = (p: PositionRow) =>
    p.status === 'OPEN' || p.status === 'ENTRY_PENDING' || p.status === 'EXIT_PENDING';
  const totalPnl = positions.reduce((s, p) => s + (p.pnl ?? 0), 0);
  const openCount = positions.filter((p) => p.status === 'OPEN' || p.status === 'EXIT_PENDING').length;
  // PENDING_PLACE (Phase 6 claim-in-flight) folds into the awaiting bucket
  // alongside TRIGGER_PENDING so the modal header doesn't under-count during
  // the ~1-3s window. Matches computeEntryChip + positionsScheduleTotals.
  const triggerPendingCount = positions.filter(
    (p) => p.entryStatus === 'TRIGGER_PENDING' || p.entryStatus === 'PENDING_PLACE',
  ).length;
  const openOrdersCount = positions.reduce((n, p) => {
    return n
      + (p.entryStatus === 'OPEN' ? 1 : 0)
      + (p.slStatus === 'OPEN' ? 1 : 0)
      + (p.exitStatus === 'OPEN' ? 1 : 0);
  }, 0);
  const rejectedAxisCount = positions.reduce((n, p) => {
    return n
      + (p.entryStatus === 'REJECTED' ? 1 : 0)
      + (p.slStatus === 'REJECTED' ? 1 : 0)
      + (p.exitStatus === 'REJECTED' ? 1 : 0);
  }, 0);
  const cancelledAxisCount = positions.reduce((n, p) => {
    return n
      + (p.entryStatus === 'CANCELLED' ? 1 : 0)
      + (p.slStatus === 'CANCELLED' ? 1 : 0)
      + (p.exitStatus === 'CANCELLED' ? 1 : 0);
  }, 0);
  const closedCount = positions.filter((p) => p.status === 'CLOSED').length;
  // Missed-exit count for the Missed box in the modal header's StatusBoxRow.
  // `p.missedExit` is set by the Schedule adapter when the backend stamps
  // `missed_exit` on today's row — absent on healthy positions and on rows
  // sourced outside the Schedule page (which never carry the field).
  const missedCount = positions.reduce(
    (n, p) => n + (p.missedExit ? 1 : 0),
    0,
  );

  // Latest entry first — mirrors the Pro strategy modal. Re-entry/re-cost
  // children fill after their parent so they naturally float to the top.
  const sortedPositions = useMemo(
    () => [...positions].sort(compareLegPositions),
    [positions],
  );

  // Bucket by executionId so STBT/BTST runs surface as separate sections
  // instead of legs from different firings interleaving by fill time. The
  // Positions-page caller leaves executionId undefined on every row, which
  // collapses to a single "" bucket — flat render, behaviour unchanged.
  const executionGroups = useMemo(
    () => groupRowsByExecution(sortedPositions),
    [sortedPositions],
  );
  const showGrouping = executionGroups.length > 1;

  // Rejected SL/exit counts aren't tracked here; the strategy-summary feed
  // owns those. Classic's header only needs an approximate SL live count
  // from per-row status, and doesn't show the SL shield unless there is
  // live SL protection — so pass 0s here (the ribbon skips the shield).
  const slCounts = useMemo(() => {
    let placed = 0;
    let rejected = 0;
    let pending = 0;
    for (const p of positions) {
      if (p.slStatus === 'TRIGGER_PENDING') placed += 1;
      else if (p.slStatus === 'REJECTED') rejected += 1;
      else if (p.slStatus === 'OPEN') pending += 1;
    }
    return { placed, rejected, pending };
  }, [positions]);

  // Derived batch-action lists — Classic only wires per-row callbacks, so
  // the "All" buttons dispatch them in a loop.
  const retryable = useMemo(
    () => positions.filter((p) => p.entryStatus === 'REJECTED' && p.positionId),
    [positions],
  );
  const exitable = useMemo(
    () => positions.filter(
      (p) => (p.status === 'OPEN' || p.status === 'EXIT_PENDING')
        && p.entryStatus !== 'REJECTED' && p.positionId,
    ),
    [positions],
  );

  // Position · Entry · Stop-Loss · Exit · P&L · Status · optional Action
  const colCount = 6 + (showActions ? 1 : 0);

  return (
    <Modal
      isOpen
      onClose={onClose}
      maxWidth="6xl"
      ariaLabel={`${strategyName} positions`}
    >
        <StrategyModalHeader
          basketName={basketName}
          strategyName={strategyName}
          isMarketplace={isMarketplace}
          legCount={positions.length}
          statusCounts={{
            open: openCount,
            pending: triggerPendingCount,
            openOrders: openOrdersCount,
            error: rejectedAxisCount,
            missed: missedCount,
            cancelled: cancelledAxisCount,
            closed: closedCount,
          }}
          slCounts={slCounts}
          totalPnl={totalPnl}
          metadata={{
            underlying: scheduleContext?.underlying || null,
            expiryType: scheduleContext?.expiryType || null,
            productType: scheduleContext?.productType || null,
            tradingType: scheduleContext?.tradingType || null,
            intradayExitMode: scheduleContext?.intradayExitMode || null,
          }}
          schedule={scheduleContext ? {
            scheduledEntryTime: scheduleContext.scheduledEntryTime,
            scheduledExitTime: scheduleContext.scheduledExitTime,
            scheduledExitDate: scheduleContext.scheduledExitDate,
            linkedEntryDate: scheduleContext.linkedEntryDate,
            viewedDate: scheduleContext.viewedDate,
            nowIstHms: scheduleContext.nowIstHms,
            brokerList: scheduleContext.brokerList,
            fillProgress: scheduleContext.fillProgress,
            runState: scheduleContext.runState,
          } : null}
          onClose={onClose}
          onBack={onBack}
          backLabel={backLabel}
          onRefresh={onRefresh}
          refreshing={refreshing}
        />

        <StrategyActionToolbar
          retryableCount={retryable.length}
          exitableCount={exitable.length}
          onRetryAll={
            // Prefer the caller's bulk handler (single rich confirm, batched
            // service call). Only fan out the per-row handler when no bulk
            // handler is wired — legacy fallback for callers that have not
            // migrated yet.
            onRetryAll
              ? () => onRetryAll(retryable.map((p) => p.positionId).filter(Boolean) as string[])
              : onRetry
                ? () => retryable.forEach((p) => onRetry(p))
                : undefined
          }
          onExitAll={
            onExitAll
              ? () => onExitAll(exitable.map((p) => p.positionId).filter(Boolean) as string[])
              : onExit
                ? () => exitable.forEach((p) => onExit(p))
                : undefined
          }
          onTerminateAll={onTerminateAll}
          pending={actionPending}
        />

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {positions.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No positions for this strategy.
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700">
                {/* Header dividers need stronger contrast — dark:gray-700
                    matches the thead background and disappears otherwise. */}
                <tr className="divide-x divide-gray-300 dark:divide-gray-600">
                  <th className={TH}>Position</th>
                  <th className={TH}>Entry</th>
                  <th className={TH}>Stop-Loss</th>
                  <th className={TH}>Exit</th>
                  <th className={TH_R}>P&amp;L</th>
                  <th className={TH_C}>Status</th>
                  {showActions && <th className={TH_C}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const renderRow = (p: PositionRow) => {
                  const displayStatus = getDisplayStatus(p);
                  const isRejected = p.entryStatus === 'REJECTED';
                  // Index-spot levels of UL legs: entry spot (Entry cell), spot SL and
                  // spot target (Stop-Loss cell — Classic has no Target column), exit spot.
                  const slState = ulSlState(p);
                  const spot = ulSpotRows(p, slState);
                  // Stop-Loss cell hint ("PTS 10" / "UL PTS 10") until a broker SL order
                  // exists — but not beside a spot SL note that already spells out the
                  // offset. With no SL at all, say so: a UL target row alone in this
                  // cell would read like a stop.
                  const slHint = p.slTriggerPrice != null || p.slLimitPrice != null ? undefined
                    : !p.slConfig ? (spot.target.length ? 'No stop-loss' : undefined)
                    : spot.sl.length && ulSlNoteHasOffset(p, slState) ? undefined
                    : slConfigDisplay(p);
                  const rowClass = cn(
                    'align-top divide-x divide-gray-200 dark:divide-gray-700',
                    rowBg(p, false),
                    rowOpacity(p),
                    'border-b border-gray-200 dark:border-gray-700',
                    'hover:bg-gray-50 dark:hover:bg-gray-700/40',
                  );

                  return (
                    <React.Fragment key={p.positionId}>
                      <tr className={rowClass}>
                        {/* Position: symbol + side/qty/re-seq/source + meta + WAT/ORB */}
                        <td className="px-3 py-3 min-w-[220px]">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">{p.symbol}</div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <SideBadge side={p.side} />
                            {p.qty > 0 && (
                              <span
                                className="text-xs tabular-nums text-gray-700 dark:text-gray-200 font-semibold"
                                title={p.lots && p.lots > 0 ? `${p.qty} qty / ${p.lots} lots` : undefined}
                              >
                                ×{formatQtyWithLots(p.qty, p.lots)}
                              </span>
                            )}
                            <ReSequenceBadge p={p} />
                            <EntrySourceBadge
                              source={p.entrySource}
                              reEntryCount={p.reEntryCount}
                              tpReexecCount={p.tpReexecCount}
                              reEntryConfig={p.reEntryConfig}
                              reExecuteConfig={p.reExecuteConfig}
                              variant="text"
                              size="xs"
                            />
                          </div>
                          <div className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">
                            {p.clientId} · L{p.legIndex ?? '—'} · {p.product}
                          </div>
                          {(p.waitAndTradeConfig || p.orbConfig || p.isHedge) && (
                            <div className="mt-1 text-[10px] leading-tight flex flex-wrap gap-x-2">
                              {p.isHedge && (
                                <span className="text-teal-700 dark:text-teal-300 font-bold uppercase tracking-wider" title="Protective hedge leg (BUY)">
                                  Hedge
                                </span>
                              )}
                              {p.waitAndTradeConfig && (() => {
                                const items = describeWatConfig(p.waitAndTradeConfig, p.entryTriggerPrice);
                                // Cell is tight — show Offset + the premium trigger. A UL W&T
                                // leg's reference / spot trigger render as labeled spot rows
                                // in the Entry cell instead (utils/ulSpotLevels).
                                const shown = items.filter((it) => it.label === 'Offset' || it.label === 'Trigger');
                                return (
                                  <span className="text-purple-700 dark:text-purple-300">
                                    <span className="font-bold uppercase tracking-wider">WAT</span>
                                    {shown.map((it) => (
                                      <React.Fragment key={it.label}>
                                        {' · '}
                                        <span className="tabular-nums">{it.value}</span>
                                      </React.Fragment>
                                    ))}
                                  </span>
                                );
                              })()}
                              {p.orbConfig && (() => {
                                const items = describeOrbConfig(p.orbConfig, p.entryTriggerPrice);
                                const range = items.find((it) => it.label === 'Range');
                                return (
                                  <span className="text-amber-700 dark:text-amber-300">
                                    <span className="font-bold uppercase tracking-wider">{orbHeaderLabel(p.orbConfig)}</span>
                                    {range && <> · <span className="tabular-nums">{range.value}</span></>}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                          {p.parentPositionId && (
                            <div className="mt-1 text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate" title={`Parent position: ${p.parentPositionId}`}>
                              ↳ {p.parentPositionId.slice(0, 16)}…
                            </div>
                          )}
                        </td>

                        {/* Entry: shared LegSection (Trigger/Expected/Limit/Filled) */}
                        <td className="px-3 py-3 min-w-[150px]">
                          <LegSection
                            variant="cell"
                            title="Entry"
                            prices={{
                              trigger: p.entryTriggerPrice,
                              expected: p.expectedEntryPrice != null && p.expectedEntryPrice !== p.entryTriggerPrice
                                ? p.expectedEntryPrice
                                : null,
                              limit: p.entryPrice,
                              filled: p.averageEntryPrice,
                            }}
                            timestamp={p.entryFilledAt ?? p.entryTime}
                            status={p.entryStatus}
                            errorMessage={p.entryError}
                            extra={
                              p.entryAttemptCount != null && p.entryAttemptCount > 1
                                ? `${p.entryAttemptCount} attempts`
                                : undefined
                            }
                            capped={
                              p.entryCircuitCapped
                                ? { placed: p.entryTriggerPrice, desired: p.entryDesiredTriggerPrice }
                                : null
                            }
                            spot={spot.entry}
                            formatTimestamp={formatLegTimestamp}
                          />
                        </td>

                        {/* Stop-Loss: shared LegSection */}
                        <td className="px-3 py-3 min-w-[150px]">
                          <LegSection
                            variant="cell"
                            title="Stop-Loss"
                            prices={{
                              trigger: p.slTriggerPrice,
                              limit: p.slLimitPrice,
                              filled: p.slAveragePrice,
                            }}
                            timestamp={p.slUpdatedAt ?? p.slPlacedAt}
                            status={p.slStatus}
                            errorMessage={p.slError}
                            extra={
                              p.slAttemptCount != null && p.slAttemptCount > 1
                                ? `${p.slAttemptCount} attempts`
                                : undefined
                            }
                            capped={
                              p.slCircuitCapped
                                ? { placed: p.slTriggerPrice, desired: p.slDesiredTriggerPrice }
                                : null
                            }
                            configHint={slHint}
                            spot={[...spot.sl, ...spot.target]}
                            formatTimestamp={formatLegTimestamp}
                          />
                        </td>

                        {/* Exit: shared LegSection. Missed-exit detection
                            (today-only, OPEN past exit time / EXIT_PENDING
                            stuck) folds into the Exit cell's errorMessage so
                            the modal renders ONE coherent red message per
                            row — no full-width sub-row, no second column. */}
                        <td className="px-3 py-3 min-w-[140px]">
                          <LegSection
                            variant="cell"
                            title="Exit"
                            prices={{
                              limit: p.exitLimitPrice,
                              filled: p.exitAveragePrice ?? p.exitPrice,
                            }}
                            timestamp={p.exitPlacedAt ?? p.exitTime}
                            status={p.exitStatus}
                            errorMessage={
                              p.missedExit
                                ? (p.exitError
                                    ? `${p.exitError} · ${missedExitLabel(p.missedExit)}`
                                    : missedExitLabel(p.missedExit))
                                : p.exitError
                            }
                            typeLabel={exitTypeLabel(p.exitType || ulExitType(p))}
                            spot={spot.exit}
                            formatTimestamp={formatLegTimestamp}
                          />
                        </td>

                        {/* P&L with LTP stacked below */}
                        <td className="px-3 py-3 text-right whitespace-nowrap align-top">
                          <PnlFlash value={p.pnl} className="block text-right text-sm font-semibold" />
                          <div className="flex items-center justify-end gap-1.5 tabular-nums mt-0.5 leading-tight">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">LTP</span>
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">
                              {formatPrice(p.ltp)}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          {displayStatus
                            ? <Badge variant={statusBadgeVariant[displayStatus] || 'warning'} size="sm">{displayStatus}</Badge>
                            : <span className="text-gray-400 dark:text-gray-500">—</span>}
                        </td>

                        {/* Actions */}
                        {showActions && (
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              {isRejected && onRetry && (
                                <button type="button" onClick={() => onRetry(p)} disabled={retryingId === p.positionId}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                  <RotateCcw className="w-3 h-3" /> Retry
                                </button>
                              )}
                              {isCancellable(p) && onExit && (
                                <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-gray-600 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                  <Ban className="w-3 h-3" /> Cancel
                                </button>
                              )}
                              {p.status === 'OPEN' && onExit && (
                                <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                  <LogOut className="w-3 h-3" /> Exit
                                </button>
                              )}
                              {isTerminable(p) && onTerminate && (
                                <button type="button" onClick={() => onTerminate(p)} disabled={terminatingId === p.positionId}
                                  title="Stop the platform from managing this position (does not touch the broker)"
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                  <Unplug className="w-3 h-3" /> Terminate
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>

                      {/* Error was duplicated here as a full-width red sub-row.
                          Removed — LegSection already renders p.entryError inline
                          inside the Entry cell. One error message per position. */}
                    </React.Fragment>
                  );
                  };

                  if (!showGrouping) return sortedPositions.map(renderRow);

                  // STBT / multi-execution: emit a spanning header row before each
                  // execution group so legs from the same firing stay visually
                  // together (yesterday's closing run vs. today's fresh entry).
                  return executionGroups.flatMap((g) => [
                    <ExecutionGroupHeaderRow
                      key={`hdr-${g.executionId || 'none'}`}
                      colSpan={colCount}
                      label={describeExecutionGroup(
                        g,
                        scheduleContext?.viewedDate || '',
                        scheduleContext?.scheduledExitDate ?? null,
                      )}
                      source={g.source}
                      legCount={g.rows.length}
                      pnl={g.pnl}
                      executionId={g.executionId}
                    />,
                    ...g.rows.map(renderRow),
                  ]);
                })()}
              </tbody>
            </table>
          )}
        </div>
    </Modal>
  );
};

/**
 * Spanning header row inserted between execution groups when an STBT/BTST
 * strategy has rows from two distinct firings on the same view day. Single
 * `<tr>` so it lives natively inside the table body (no DOM nesting issues)
 * and a `colSpan` so the chips span the full width without disturbing the
 * column grid.
 */
const ExecutionGroupHeaderRow: React.FC<{
  colSpan: number;
  label: string;
  source: 'entered' | 'closed' | 'carried' | null;
  legCount: number;
  pnl: number;
  executionId: string;
}> = ({ colSpan, label, source, legCount, pnl, executionId }) => {
  const sourcePill = sourcePillStyle(source);
  return (
    <tr className="bg-gray-50 dark:bg-gray-800/40 border-y border-gray-200 dark:border-gray-700">
      <td colSpan={colSpan} className="px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-200">
            {label}
          </span>
          {sourcePill && (
            <span className={cn(
              'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider',
              sourcePill.className,
            )}>
              {sourcePill.text}
            </span>
          )}
          <span className="text-[10px] text-gray-500 dark:text-gray-400">
            · {legCount} leg{legCount !== 1 ? 's' : ''}
          </span>
          <span className="text-[11px] font-semibold">
            · <PnlFlash value={pnl} className="text-[11px] font-semibold" />
          </span>
          {executionId && !executionId.startsWith('legacy_') && (
            <span className="ml-auto text-[10px] font-mono text-gray-400 dark:text-gray-500" title={executionId}>
              {shortExecutionId(executionId)}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
};

function sourcePillStyle(source: 'entered' | 'closed' | 'carried' | null): { text: string; className: string } | null {
  switch (source) {
    case 'entered':
      return { text: 'Entered', className: CHIP_DENSE.SOURCE_ENTERED };
    case 'closed':
      return { text: 'Closed', className: CHIP_DENSE.SOURCE_CLOSED };
    case 'carried':
      return { text: 'Carry', className: CHIP_DENSE.SOURCE_CARRY };
    default:
      return null;
  }
}

// compareLegPositions moved to positionDisplay.tsx (pure util, no component deps)
// so tests can import it without touching the component's transitive import chain.
// Re-exported here for backwards-compat with any caller that imports from this file.
export { compareLegPositions } from '../../utils/positionDisplay';

export default StrategyPositionsModal;
